import type { DashboardStatus } from '../../../services/strategyService';

interface SetupRequiredOverlayProps {
  dashboardStatus: DashboardStatus;
  setActiveSection: (section: string) => void;
}

export default function SetupRequiredOverlay({ dashboardStatus, setActiveSection }: SetupRequiredOverlayProps) {
  const steps = [
    {
      key: 'keywords',
      icon: 'ri-key-2-line',
      title: 'Lock Keyword Universe',
      description: 'Select and lock your core search keywords',
      sectionStatus: dashboardStatus.sections.keywords.status,
      detail: dashboardStatus.sections.keywords.count > 0
        ? `${dashboardStatus.sections.keywords.count} keywords`
        : undefined,
      route: 'utility-manager',
    },
    {
      key: 'asis',
      icon: 'ri-file-list-line',
      title: 'View AS-IS State',
      description: 'Analyze current SEO performance baseline',
      sectionStatus: dashboardStatus.sections.asis.status,
      detail: dashboardStatus.sections.asis.age_days !== null
        ? `${dashboardStatus.sections.asis.age_days} days ago`
        : undefined,
      route: 'as-is-state',
    },
    {
      key: 'goals',
      icon: 'ri-flag-line',
      title: 'Set Goals',
      description: 'Define 90-day objectives',
      sectionStatus: dashboardStatus.sections.goals.status,
      detail: dashboardStatus.sections.goals.count > 0
        ? `${dashboardStatus.sections.goals.count} goals`
        : undefined,
      route: 'goal-setting',
    },
    {
      key: 'action_plan',
      icon: 'ri-task-line',
      title: 'Generate Action Plan',
      description: 'Create implementation roadmap',
      sectionStatus: dashboardStatus.sections.action_plan.status,
      detail: dashboardStatus.sections.action_plan.count > 0
        ? `${dashboardStatus.sections.action_plan.count} tasks`
        : undefined,
      route: 'action-plan',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Next 3 Months Strategy Snapshot
        </h2>
      </div>

      <div className="relative">
        {/* Blurred background */}
        <div className="filter blur-sm pointer-events-none opacity-40">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-6 h-40"></div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl p-6 h-40"></div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl p-6 h-40"></div>
          </div>
        </div>

        {/* Overlay with setup steps */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-2xl border-2 border-teal-500 max-w-2xl w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-information-line text-3xl text-teal-600 dark:text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Check the individual pages to populate strategy summary
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {dashboardStatus.next_step.title}: {dashboardStatus.next_step.description}
              </p>
            </div>

            {/* Progress Steps */}
            <div className="space-y-3 mb-6">
              {steps.map((step, index) => {
                const isComplete = step.sectionStatus === 'complete';

                return (
                  <div
                    key={step.key}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                      isComplete
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isComplete
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                      }`}>
                        {isComplete ? (
                          <i className="ri-check-line"></i>
                        ) : (
                          <span className="text-sm font-semibold">{index + 1}</span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {step.title}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {step.description}
                          {isComplete && step.detail && (
                            <span className="ml-2 text-teal-600 dark:text-teal-400">
                              ({step.detail})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {isComplete ? (
                      <i className="ri-check-double-line text-2xl text-green-500"></i>
                    ) : (
                      <button
                        onClick={() => setActiveSection(step.route)}
                        className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium"
                      >
                        Start
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Next Action Button */}
            {dashboardStatus.next_step.action && dashboardStatus.next_step.route && (
              <button
                onClick={() => setActiveSection(dashboardStatus.next_step.route!)}
                className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-teal-600 hover:to-emerald-600 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>{dashboardStatus.next_step.action}</span>
                <i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
